// src/marketing/content/useContents.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type {
  Content, ContentFormat, ContentKind, ContentPlatform, ContentStatus, TemplateLanguage,
} from '../../lib/types'

const contentKeys = {
  all: ['contents'] as const,
  list: (appId: string) => ['contents', 'list', appId] as const,
  detail: (id: string) => ['contents', 'detail', id] as const,
}

// `| undefined` explicite : exactOptionalPropertyTypes est activé.
export type ContentFilters = {
  kind?: ContentKind | '' | undefined
  platform?: ContentPlatform | '' | undefined
  status?: ContentStatus | '' | undefined
  language?: TemplateLanguage | '' | undefined
  campaignId?: string | undefined
}

export function useContents(appId: string | null, filters: ContentFilters = {}) {
  const params = new URLSearchParams()
  if (appId) params.set('appId', appId)
  if (filters.kind) params.set('kind', filters.kind)
  if (filters.platform) params.set('platform', filters.platform)
  if (filters.status) params.set('status', filters.status)
  if (filters.language) params.set('language', filters.language)
  if (filters.campaignId) params.set('campaignId', filters.campaignId)
  return useQuery<Content[], Error>({
    queryKey: [
      ...contentKeys.list(appId ?? ''),
      filters.kind ?? '', filters.platform ?? '', filters.status ?? '',
      filters.language ?? '', filters.campaignId ?? '',
    ],
    queryFn: () =>
      api.get<{ contents: Content[] }>(`/api/contents?${params.toString()}`).then((r) => r.contents),
    enabled: Boolean(appId),
    staleTime: 1000 * 60 * 2,
  })
}

export function useContent(id: string | null) {
  return useQuery<Content, Error>({
    queryKey: contentKeys.detail(id ?? ''),
    queryFn: () => api.get<{ content: Content }>(`/api/contents/${id}`).then((r) => r.content),
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 2,
  })
}

export type CreateContentInput = {
  appId: string
  title: string
  kind: ContentKind
  format?: ContentFormat | undefined
  platform: ContentPlatform
  language?: TemplateLanguage | null | undefined
  status?: ContentStatus | undefined
  hook?: string | null | undefined
  bodyMd?: string | undefined
  url?: string | null | undefined
  plannedDate?: string | null | undefined
  liveDate?: string | null | undefined
  budgetAllocated?: number | null | undefined
  campaignId?: string | null | undefined
  creatorId?: string | null | undefined
  views?: number | null | undefined
  notes?: string | null | undefined
}

export type UpdateContentInput = Omit<Partial<CreateContentInput>, 'appId'>

export function useCreateContent() {
  const queryClient = useQueryClient()
  return useMutation<Content, Error, CreateContentInput>({
    mutationFn: (body) =>
      api.post<{ content: Content }>('/api/contents', body).then((r) => r.content),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contentKeys.all })
    },
  })
}

export function useUpdateContent(id: string) {
  const queryClient = useQueryClient()
  return useMutation<Content, Error, UpdateContentInput>({
    mutationFn: (body) =>
      api.put<{ content: Content }>(`/api/contents/${id}`, body).then((r) => r.content),
    onSuccess: (updated) => {
      queryClient.setQueryData(contentKeys.detail(id), updated)
      void queryClient.invalidateQueries({ queryKey: contentKeys.all })
    },
  })
}

// Optimistic update + rollback, comme usePatchDealStage (useDeals.ts) : le kanban doit
// refléter le drop immédiatement. Différence avec le Pipeline : la clé de liste des
// contenus embarque les filtres, donc on agit sur TOUTES les listes de l'app via
// setQueriesData (préfixe ['contents','list',appId]) et non sur une clé unique.
export function usePatchContentStatus(appId: string | null) {
  const qc = useQueryClient()
  const listPrefix = contentKeys.list(appId ?? '')
  return useMutation<
    Content,
    Error,
    { id: string; status: ContentStatus },
    { previous: [readonly unknown[], Content[] | undefined][] }
  >({
    mutationFn: ({ id, status }) =>
      api.patch<{ content: Content }>(`/api/contents/${id}`, { status }).then((r) => r.content),
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: listPrefix })
      const previous = qc.getQueriesData<Content[]>({ queryKey: listPrefix })
      qc.setQueriesData<Content[]>({ queryKey: listPrefix }, (old) =>
        old?.map((c) => (c.id === id ? { ...c, status } : c)) ?? [],
      )
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, data] of ctx?.previous ?? []) {
        qc.setQueryData(key, data)
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: contentKeys.all })
    },
  })
}

export function useDeleteContent() {
  const queryClient = useQueryClient()
  // L'endpoint renvoie `{ ok: true }` et handleResponse parse toujours le corps —
  // typer `void` serait un mensonge ; on reprend le typage de useDeleteDeal.
  return useMutation<{ ok: boolean }, Error, string>({
    mutationFn: (id) => api.del<{ ok: boolean }>(`/api/contents/${id}`),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: contentKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: contentKeys.all })
    },
  })
}
